import React, { useState } from "react";
import { api } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { MapPin, Landmark, Plus, X, Pencil, Save, Trash2 } from "lucide-react";
import { DMS_SUGGESTIONS, MENU_SUGGESTIONS, emptyLocation, emptyAch } from "@/components/DealerIntakeForm";

/**
 * Admin-side locations + ACH manager. Renders on `DealerDetail` under the
 * signers builder so admins can add/edit/delete rooftops and their ACH
 * groups without leaving the dealer page.
 *
 * Props:
 *   dealerId — MongoDB _id
 *   locations — current locations[] on the dealer doc
 *   achGroups — current ach_groups[] on the dealer doc
 *   onChange() — parent reload callback (refreshes both arrays)
 */
export default function LocationsPanel({ dealerId, locations = [], achGroups = [], onChange }) {
  const [addingLoc, setAddingLoc] = useState(false);
  const [locDraft, setLocDraft] = useState(null);
  const [editingLoc, setEditingLoc] = useState(null); // id being edited
  const [locEdit, setLocEdit] = useState({});

  const [addingAch, setAddingAch] = useState(false);
  const [achDraft, setAchDraft] = useState(null);
  const [editingAch, setEditingAch] = useState(null);
  const [achEdit, setAchEdit] = useState({});

  // ================= Locations =================
  const startAddLoc = () => { setLocDraft(emptyLocation()); setAddingLoc(true); };
  const cancelAddLoc = () => { setLocDraft(null); setAddingLoc(false); };
  const submitNewLoc = async () => {
    try {
      await api.post(`/dealers/${dealerId}/locations`, locDraft);
      toast.success("Location added");
      cancelAddLoc();
      onChange?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const startEditLoc = (loc) => { setEditingLoc(loc.id); setLocEdit({ ...loc, fi_managers: loc.fi_managers?.length ? loc.fi_managers : [""] }); };
  const cancelEditLoc = () => { setEditingLoc(null); setLocEdit({}); };
  const submitEditLoc = async () => {
    try {
      await api.put(`/dealers/${dealerId}/locations/${editingLoc}`, locEdit);
      toast.success("Location updated");
      cancelEditLoc();
      onChange?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const deleteLoc = async (loc) => {
    if (!window.confirm(`Delete location "${loc.name || loc.physical_address || 'unnamed'}"? Any ACH group covering it will be unlinked.`)) return;
    try {
      await api.delete(`/dealers/${dealerId}/locations/${loc.id}`);
      toast.success("Location removed");
      onChange?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  // ================= ACH =================
  const startAddAch = () => { setAchDraft(emptyAch()); setAddingAch(true); };
  const cancelAddAch = () => { setAchDraft(null); setAddingAch(false); };
  const submitNewAch = async () => {
    try {
      // ACH server-side expects real location ids — translate.
      const payload = { ...achDraft, location_ids: (achDraft.location_ids || []).map((i) => locations[i]?.id).filter(Boolean) };
      await api.post(`/dealers/${dealerId}/ach-groups`, payload);
      toast.success("ACH added");
      cancelAddAch();
      onChange?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const startEditAch = (g) => { setEditingAch(g.id); setAchEdit({ ...g, location_ids: g.location_ids || [] }); };
  const cancelEditAch = () => { setEditingAch(null); setAchEdit({}); };
  const submitEditAch = async () => {
    try {
      await api.put(`/dealers/${dealerId}/ach-groups/${editingAch}`, achEdit);
      toast.success("ACH updated");
      cancelEditAch();
      onChange?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const deleteAch = async (g) => {
    if (!window.confirm(`Delete ACH "${g.label || 'unlabeled'}"?`)) return;
    try {
      await api.delete(`/dealers/${dealerId}/ach-groups/${g.id}`);
      toast.success("ACH removed");
      onChange?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <Card className="p-6 border-[#E9ECEF] rounded-sm mb-6" data-testid="locations-panel">
      {/* Locations header + list */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-[#0B132B]">
          <MapPin size={14} className="text-[#D4AF37]" /> Locations ({locations.length})
        </div>
        {!addingLoc && (
          <Button onClick={startAddLoc} size="sm" className="h-9 rounded-sm bg-[#0B132B] hover:bg-[#1C2541] text-white" data-testid="loc-panel-add">
            <Plus size={13} className="mr-1.5" /> Add
          </Button>
        )}
      </div>

      {locations.length === 0 && !addingLoc && (
        <div className="text-sm italic text-[#6C757D] py-6 text-center border border-dashed border-[#E9ECEF] rounded-sm">
          No locations yet.
        </div>
      )}

      <div className="space-y-2 mb-4">
        {locations.map((loc) => (
          <div key={loc.id} className="border border-[#E9ECEF] rounded-sm bg-white" data-testid={`loc-row-${loc.id}`}>
            {editingLoc === loc.id ? (
              <LocationEditor draft={locEdit} setDraft={setLocEdit} onCancel={cancelEditLoc} onSave={submitEditLoc} />
            ) : (
              <div className="p-3 flex items-center gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold text-[#0B132B]">{loc.name || <span className="italic text-[#6C757D]">Unnamed store</span>}</div>
                  <div className="text-[11px] text-[#6C757D] truncate">
                    {[loc.physical_address, loc.phone, loc.ein && `EIN ${loc.ein}`, loc.general_manager && `GM: ${loc.general_manager}`, loc.labor_rate && `$${loc.labor_rate}/hr`, loc.dms, loc.menu_provider].filter(Boolean).join(" · ")}
                  </div>
                  {(loc.fi_managers || []).length > 0 && (
                    <div className="text-[10px] text-[#6C757D] mt-0.5">F&I: {(loc.fi_managers || []).join(", ")}</div>
                  )}
                </div>
                <Button size="sm" variant="outline" onClick={() => startEditLoc(loc)} className="h-8 rounded-sm" data-testid={`loc-edit-${loc.id}`}><Pencil size={12} /></Button>
                <Button size="sm" variant="outline" onClick={() => deleteLoc(loc)} className="h-8 rounded-sm border-red-200 text-red-600 hover:bg-red-50" data-testid={`loc-delete-${loc.id}`}><Trash2 size={12} /></Button>
              </div>
            )}
          </div>
        ))}
      </div>

      {addingLoc && (
        <div className="border-2 border-[#D4AF37] rounded-sm bg-[#F8F9FA] mb-4" data-testid="loc-panel-new-form">
          <LocationEditor draft={locDraft} setDraft={setLocDraft} onCancel={cancelAddLoc} onSave={submitNewLoc} isNew />
        </div>
      )}

      {/* ACH header + list */}
      <div className="border-t border-[#E9ECEF] pt-4 mt-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-[#0B132B]">
            <Landmark size={14} className="text-[#D4AF37]" /> ACH ({achGroups.length})
          </div>
          {!addingAch && (
            <Button onClick={startAddAch} size="sm" disabled={locations.length === 0}
              className="h-9 rounded-sm bg-[#0B132B] hover:bg-[#1C2541] text-white" data-testid="ach-panel-add">
              <Plus size={13} className="mr-1.5" /> Add
            </Button>
          )}
        </div>

        {achGroups.length === 0 && !addingAch && (
          <div className="text-sm italic text-[#6C757D] py-6 text-center border border-dashed border-[#E9ECEF] rounded-sm">
            No ACH records yet.
          </div>
        )}

        <div className="space-y-2 mb-4">
          {achGroups.map((g) => (
            <div key={g.id} className="border border-[#E9ECEF] rounded-sm bg-white" data-testid={`ach-row-${g.id}`}>
              {editingAch === g.id ? (
                <AchEditor draft={achEdit} setDraft={setAchEdit} locations={locations} onCancel={cancelEditAch} onSave={submitEditAch} />
              ) : (
                <div className="p-3 flex items-center gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold text-[#0B132B]">{g.label || <span className="italic text-[#6C757D]">Unlabeled ACH</span>}</div>
                    <div className="text-[11px] text-[#6C757D] truncate">
                      {[g.bank_name, g.account && `••${(g.account || "").slice(-4)}`, g.routing && `RTN ${(g.routing || "").slice(-4)}`].filter(Boolean).join(" · ")}
                    </div>
                    <div className="text-[10px] text-[#6C757D] mt-0.5">
                      Covers: {(g.location_ids || []).map((lid) => (locations.find((l) => l.id === lid)?.name || lid)).join(", ") || <em>no locations</em>}
                    </div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => startEditAch(g)} className="h-8 rounded-sm" data-testid={`ach-edit-${g.id}`}><Pencil size={12} /></Button>
                  <Button size="sm" variant="outline" onClick={() => deleteAch(g)} className="h-8 rounded-sm border-red-200 text-red-600 hover:bg-red-50" data-testid={`ach-delete-${g.id}`}><Trash2 size={12} /></Button>
                </div>
              )}
            </div>
          ))}
        </div>

        {addingAch && (
          <div className="border-2 border-[#D4AF37] rounded-sm bg-[#F8F9FA]" data-testid="ach-panel-new-form">
            <AchEditor draft={achDraft} setDraft={setAchDraft} locations={locations} onCancel={cancelAddAch} onSave={submitNewAch} isNew />
          </div>
        )}
      </div>
    </Card>
  );
}

// -------- Location editor sub-component --------
function LocationEditor({ draft, setDraft, onCancel, onSave, isNew }) {
  const set = (patch) => setDraft({ ...draft, ...patch });
  const patchFi = (idx, name) => {
    const arr = [...(draft.fi_managers || [""])];
    arr[idx] = name;
    set({ fi_managers: arr });
  };
  const addFi = () => set({ fi_managers: [...(draft.fi_managers || []), ""] });
  const removeFi = (idx) => {
    const arr = [...(draft.fi_managers || [""])];
    arr.splice(idx, 1);
    set({ fi_managers: arr.length ? arr : [""] });
  };
  return (
    <div className="p-4 space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Input value={draft.name || ""} onChange={(e) => set({ name: e.target.value })} placeholder="Store name" data-testid="loc-edit-name" />
        <Input value={draft.phone || ""} onChange={(e) => set({ phone: e.target.value })} placeholder="Location phone" data-testid="loc-edit-phone" />
        <Input value={draft.physical_address || ""} onChange={(e) => set({ physical_address: e.target.value })} placeholder="Street, City, State ZIP" className="sm:col-span-2" data-testid="loc-edit-address" />
        <Input value={draft.general_manager || ""} onChange={(e) => set({ general_manager: e.target.value })} placeholder="General Manager" data-testid="loc-edit-gm" />
        <Input value={draft.fi_director || ""} onChange={(e) => set({ fi_director: e.target.value })} placeholder="F&I Director (optional)" data-testid="loc-edit-fi-director" />
        <Input value={draft.office_manager || ""} onChange={(e) => set({ office_manager: e.target.value })} placeholder="Office Manager" data-testid="loc-edit-office-mgr" />
        <Input value={draft.labor_rate || ""} onChange={(e) => set({ labor_rate: e.target.value })} placeholder="Labor rate ($/hr)" data-testid="loc-edit-labor-rate" />
      </div>
      <div>
        <div className="flex items-center justify-between mb-1">
          <Label className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#0B132B]">F&I Managers</Label>
          <button type="button" onClick={addFi} className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#D4AF37]" data-testid="loc-edit-add-fi">+ Add</button>
        </div>
        {(draft.fi_managers || [""]).map((m, i) => (
          <div key={i} className="flex items-center gap-2 mb-1">
            <Input value={m} onChange={(e) => patchFi(i, e.target.value)} placeholder={`F&I Manager #${i + 1}`} className="flex-1" data-testid={`loc-edit-fi-${i}`} />
            {(draft.fi_managers || []).length > 1 && (
              <button type="button" onClick={() => removeFi(i)} className="text-[#6C757D] hover:text-red-600" aria-label="Remove"><X size={14} /></button>
            )}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#0B132B]">DMS</Label>
          <Input list="dms-list-panel" value={draft.dms || ""} onChange={(e) => set({ dms: e.target.value })} placeholder="e.g. CDK Global" data-testid="loc-edit-dms" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#0B132B]">Menu</Label>
          <Input list="menu-list-panel" value={draft.menu_provider || ""} onChange={(e) => set({ menu_provider: e.target.value })} placeholder="e.g. MenuMetric" data-testid="loc-edit-menu" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#0B132B]">Location EIN</Label>
          <Input value={draft.ein || ""} onChange={(e) => set({ ein: e.target.value })} placeholder="If different from dealer EIN" data-testid="loc-edit-ein" />
        </div>
        <datalist id="dms-list-panel">{DMS_SUGGESTIONS.map((s) => <option key={s} value={s} />)}</datalist>
        <datalist id="menu-list-panel">{MENU_SUGGESTIONS.map((s) => <option key={s} value={s} />)}</datalist>
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <Button variant="outline" size="sm" onClick={onCancel} className="h-9 rounded-sm">Cancel</Button>
        <Button size="sm" onClick={onSave} className="h-9 rounded-sm bg-[#0B132B] hover:bg-[#1C2541] text-white" data-testid={isNew ? "loc-edit-save-new" : "loc-edit-save"}>
          <Save size={12} className="mr-1.5" /> {isNew ? "Add location" : "Save"}
        </Button>
      </div>
    </div>
  );
}

// -------- ACH editor sub-component --------
function AchEditor({ draft, setDraft, locations, onCancel, onSave, isNew }) {
  const set = (patch) => setDraft({ ...draft, ...patch });
  // For NEW we track locations by index (ids don't exist yet); for EDIT we
  // track by id. Detect via whether every entry looks like a number.
  const usingIndex = isNew;
  const isSelected = (loc, idx) => {
    const ids = draft.location_ids || [];
    return usingIndex ? ids.includes(idx) : ids.includes(loc.id);
  };
  const toggle = (loc, idx) => {
    const cur = new Set(draft.location_ids || []);
    const key = usingIndex ? idx : loc.id;
    if (cur.has(key)) cur.delete(key); else cur.add(key);
    set({ location_ids: Array.from(cur) });
  };
  return (
    <div className="p-4 space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Input value={draft.label || ""} onChange={(e) => set({ label: e.target.value })} placeholder="Label (e.g. Main ACH)" data-testid="ach-edit-label" />
        <Input value={draft.account_holder || ""} onChange={(e) => set({ account_holder: e.target.value })} placeholder="Account holder" data-testid="ach-edit-holder" />
        <Input value={draft.bank_name || ""} onChange={(e) => set({ bank_name: e.target.value })} placeholder="Bank name" data-testid="ach-edit-bank" />
        <Input value={draft.routing || ""} onChange={(e) => set({ routing: e.target.value })} placeholder="Routing #" data-testid="ach-edit-routing" />
        <Input value={draft.account || ""} onChange={(e) => set({ account: e.target.value })} placeholder="Account #" data-testid="ach-edit-account" />
      </div>
      <div>
        <Label className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#0B132B] mb-2 block">
          Locations covered
        </Label>
        {locations.length === 0 ? (
          <div className="text-xs italic text-[#6C757D]">Add a location first.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {locations.map((loc, idx) => {
              const on = isSelected(loc, idx);
              return (
                <label key={loc.id || idx}
                       className={`flex items-center gap-2 p-2 rounded-sm border-2 cursor-pointer ${on ? "border-[#D4AF37] bg-[#D4AF37]/5" : "border-[#E9ECEF] hover:border-[#0B132B]/30"}`}
                       data-testid={`ach-edit-loc-${idx}`}>
                  <Checkbox checked={on} onCheckedChange={() => toggle(loc, idx)} />
                  <span className="text-sm text-[#0B132B]">{loc.name || `Location ${idx + 1}`}</span>
                </label>
              );
            })}
          </div>
        )}
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <Button variant="outline" size="sm" onClick={onCancel} className="h-9 rounded-sm">Cancel</Button>
        <Button size="sm" onClick={onSave} className="h-9 rounded-sm bg-[#0B132B] hover:bg-[#1C2541] text-white" data-testid={isNew ? "ach-edit-save-new" : "ach-edit-save"}>
          <Save size={12} className="mr-1.5" /> {isNew ? "Add ACH" : "Save"}
        </Button>
      </div>
    </div>
  );
}
